import React from 'react';

import classNames from 'classnames';

import {Icon} from './../../icon/icon';
import {cellTooltip, ITableCellProps} from './table';

export interface ITableRowProps {
    cells: ITableCellProps[];
    className?: string;
    depth?: number;
    focusedColumns?: number[];
    hoveringColumn?: number;
    id?: TTableRowId;
    isFocused?: boolean;
    isHovering?: boolean;
    isOpen?: boolean;
    onHover?: (id?: TTableRowId, column?: number) => void;
    onClick?: () => void;
    isVisible?: boolean;
    isTextGreyedOut?: boolean;
    siblingHasChildren?: boolean;
    subRows?: ITableRowProps[];
    isTextBold?: boolean;
}

export type TTableRowId = string | number;

export const TableRow = (props: ITableRowProps): JSX.Element => {
    const element: React.MutableRefObject<null | HTMLElement> = React.useRef(null);
    const hasChildren: boolean = !!(props.subRows && props.subRows.length);
    const depth: number = props.depth ? props.depth : 0;
    const className: string = classNames(
        'table__row',
        {
            [`table__row--depth-${depth}`]: !!depth,
            'table__row--sibling-has-children': props.siblingHasChildren,
            'is-open': props.isOpen,
            'is-focused': props.isFocused,
            'is-hovering': props.isHovering,
            'has-children': hasChildren,
            'is-visible': props.isVisible,
            'is-grey': props.isTextGreyedOut,
            'is-bold': props.isTextBold,
        },
        props.className,
    );

    const onMouseEnter = (row: TTableRowId | undefined, column: number): void => {
        if (props.onHover) {
            props.onHover(row, column);
        }
    };

    const onMouseLeave = (): void => {
        if (props.onHover) {
            props.onHover(undefined, undefined);
        }
    };

    return (
        <tr
            className={className}
            ref={(el: HTMLElement | null): void => {
                if (el && element.current !== el) {
                    element.current = el;
                }
            }}
        >
            {props.cells.map((cell: ITableCellProps, j: number) => {
                const isFocused: boolean = !!(props.focusedColumns && props.focusedColumns.includes(j));
                const isHovering: boolean = !!(props.hoveringColumn && props.hoveringColumn === j);
                const cellClassName: string = classNames(
                    'table__cell',
                    {
                        'is-focused': isFocused,
                        'is-hovering': isHovering,
                    },
                );

                let additionalPixels;

                if (props.siblingHasChildren && !hasChildren) {
                    additionalPixels = 24;
                } else if (!props.siblingHasChildren) {
                    additionalPixels = 16;
                } else {
                    additionalPixels = 0;
                }

                return (
                    <td
                        className={cellClassName}
                        key={j}
                        onMouseEnter={(): void => onMouseEnter(props.id, j)}
                        onMouseLeave={(): void => onMouseLeave()}
                        onClick={j === 0 ? props.onClick : undefined}
                        style={(props.depth && j === 0) ?
                            {
                                paddingLeft: props.depth * 32 + additionalPixels + 'px',
                            }
                            : undefined}
                    >
                        {isFocused && <div className="table__cell-focus" />}
                        {isHovering && <div className="table__cell-hover" />}
                        <div className="table__cell-inner">
                            {hasChildren && j === 0 &&
                                <Icon className="table__icon" name="caret-right" />
                            }
                            {cell.tooltip ? cellTooltip(cell.content, cell.tooltip) : cell.content}
                        </div>
                    </td>
                );
            })}
        </tr>
    );
};
